import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { toast } from "sonner"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { api } from "@/lib/client"
import { formatAddress, formatDateLabel, formatModifiers, formatMoney, windowLabel } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { statusBadgeClass } from "@/lib/table"
import {
  DELIVERY_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  type DeliveryStatus,
  type OrderStatus,
} from "@/lib/types"

export function OrderDetailPage() {
  const { orderId = "" } = useParams()
  const queryClient = useQueryClient()
  const orderQuery = useQuery({
    queryFn: () => api.order(orderId),
    queryKey: queryKeys.order(orderId),
  })
  const order = orderQuery.data?.order

  const patchOrder = useMutation({
    mutationFn: (status: OrderStatus) => api.patchOrderStatus(Number(orderId), status),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Order updated")
      await queryClient.invalidateQueries()
    },
  })
  const patchDelivery = useMutation({
    mutationFn: ({ date, status }: { date: string; status: DeliveryStatus }) =>
      api.patchDeliveryStatus(Number(orderId), date, status),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Delivery updated")
      await queryClient.invalidateQueries()
    },
  })

  if (orderQuery.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (!order) return <p className="text-sm text-destructive">Order not found.</p>

  return (
    <div className="space-y-6">
      <AdminPageHeader
        description={`${order.customerName || "Customer"} · ${formatDateLabel(order.weekStartDate)} – ${formatDateLabel(order.weekEndDate)}`}
        title={order.name}
      >
        <Button asChild size="sm" variant="outline">
          <Link to={`/orders/${order.id}/invoice`}>Invoice</Link>
        </Button>
        <Select
          onValueChange={(v) => patchOrder.mutate(v as OrderStatus)}
          value={order.state}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </AdminPageHeader>

      <div className="flex flex-wrap gap-4 text-sm">
        <span className={statusBadgeClass(order.state)}>
          {ORDER_STATUS_LABELS[order.state as OrderStatus] ?? order.state}
        </span>
        <span>{formatMoney(order.amountTotal, order.currency)}</span>
        {order.customerId ? (
          <Link className="text-muted-foreground hover:underline" to={`/customers/${order.customerId}`}>
            {order.customerEmail}
          </Link>
        ) : null}
      </div>
      {order.note ? <p className="text-sm text-muted-foreground whitespace-pre-wrap">{order.note}</p> : null}

      <div className="space-y-4">
        {order.deliveries.map((delivery) => (
          <Card key={delivery.date}>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">{formatDateLabel(delivery.date)}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {windowLabel(delivery.window)} · {formatAddress(delivery.address)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button asChild size="sm" variant="ghost">
                  <Link to={`/orders/${order.id}/work-order/${delivery.date}`}>Work order</Link>
                </Button>
                <Select
                  onValueChange={(v) =>
                    patchDelivery.mutate({ date: delivery.date, status: v as DeliveryStatus })
                  }
                  value={delivery.state}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DELIVERY_STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {delivery.lines.map((line) => (
                  <li key={line.id}>
                    <span className="font-medium">{line.quantity}×</span> {line.name}
                    {line.mealTime ? <span className="text-muted-foreground"> · {line.mealTime}</span> : null}
                    {formatModifiers(line.modifiers) ? (
                      <span className="text-muted-foreground"> · {formatModifiers(line.modifiers)}</span>
                    ) : null}
                    <span className="float-right text-muted-foreground">
                      {formatMoney(line.priceSubtotal, order.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
